import { Suspense, useState, type ReactNode } from "react";

import { TabPanelFallback } from "@/components/layout/AppDetailTabs";
import { PanelToolbarProvider } from "@/components/layout/PanelToolbar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  segmentedTabTriggerClass,
  segmentedTabsListClass,
} from "@/components/ui/tabs";
import type { BackgroundCheckChip } from "@/pages/admin/adminAppDetailTab";

interface BackgroundCheckTabProps {
  chip: BackgroundCheckChip;
  onChipChange: (chip: BackgroundCheckChip) => void;
  /** The application has detected default history. */
  historyBadge: boolean;
  kyb: ReactNode;
  history: ReactNode;
}

/** Background Check: Default History and KYB/KYC (Baselayer), switched by chips. */
function BackgroundCheckTab({
  chip,
  onChipChange,
  historyBadge,
  kyb,
  history,
}: BackgroundCheckTabProps) {
  // The active panel puts its actions (PanelToolbar) in this row, right of the chips.
  const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
  return (
    <Tabs value={chip} onValueChange={(value) => onChipChange(value as BackgroundCheckChip)}>
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <TabsList className={segmentedTabsListClass} aria-label="Background check">
          <TabsTrigger value="history" className={segmentedTabTriggerClass}>
            Default History
            {historyBadge && (
              <span
                role="img"
                aria-label="Default history found"
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-destructive"
              />
            )}
          </TabsTrigger>
          <TabsTrigger value="kyb" className={segmentedTabTriggerClass}>
            KYB/KYC
          </TabsTrigger>
        </TabsList>
        <div
          ref={setToolbar}
          data-testid="background-check-toolbar"
          className="ml-auto flex min-h-8 flex-wrap items-center justify-end gap-3"
        />
      </div>
      <PanelToolbarProvider value={toolbar}>
        {/* Each panel loads lazily on its own, so the chips stay put while it does. */}
        <TabsContent value="history" className="mt-0">
          <Suspense fallback={<TabPanelFallback />}>{history}</Suspense>
        </TabsContent>
        <TabsContent value="kyb" className="mt-0">
          <Suspense fallback={<TabPanelFallback />}>{kyb}</Suspense>
        </TabsContent>
      </PanelToolbarProvider>
    </Tabs>
  );
}

export default BackgroundCheckTab;
